import { useContext, useEffect, useRef, useState } from 'react';
import { BellRing, CheckCircle2, CircleAlert, Smartphone } from 'lucide-react';
import { apiClient, AuthContext } from '../App';
import { getPushPermissionState, enablePushNotifications } from '../lib/pushNotifications';

const PERMISSION_COPY = {
  granted: {
    label: 'Permission granted on this device',
    icon: CheckCircle2,
    color: 'text-green-700',
  },
  denied: {
    label: 'Permission denied on this device',
    icon: CircleAlert,
    color: 'text-amber-700',
  },
  prompt: {
    label: 'Permission has not been requested on this device',
    icon: CircleAlert,
    color: 'text-zinc-600',
  },
  unknown: {
    label: 'Notification permission could not be checked. Its current state is unknown.',
    icon: CircleAlert,
    color: 'text-amber-700',
  },
  unavailable: {
    label: 'Notification permission cannot be checked on this platform',
    icon: Smartphone,
    color: 'text-zinc-600',
  },
};

const PushNotificationSettings = () => {
  const { user } = useContext(AuthContext) || {};
  const context = useRef(0);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [permission, setPermission] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [registrationError, setRegistrationError] = useState(false);

  useEffect(() => {
    let active = true;
    context.current += 1;
    const readContext = context.current;
    busyRef.current = false;
    setBusy(false);
    setResult(null);
    setPermission(null);
    setRegistration(null);
    setRegistrationError(false);

    const load = async () => {
      const permissionState = await getPushPermissionState();
      if (active && context.current === readContext) setPermission(permissionState);

      try {
        const response = await apiClient.get('/push/status');
        if (active && context.current === readContext) {
          setRegistration(response.data?.active_device_registered === true);
          setRegistrationError(false);
        }
      } catch (error) {
        console.error('Failed to load push registration status:', error);
        if (active && context.current === readContext) setRegistrationError(true);
      }
    };

    load();
    return () => {
      active = false;
      context.current += 1;
    };
  }, [user?.id]);

  const retryPermissionCheck = async () => {
    if (busyRef.current) return;
    const started = context.current;
    busyRef.current = true;
    setBusy(true);
    try {
      const state = await getPushPermissionState();
      if (context.current === started) {
        setPermission(state);
        setResult(previous => ['unknown', 'unavailable'].includes(previous?.code) ? null : previous);
      }
    } finally {
      if (context.current === started) { busyRef.current = false; setBusy(false); }
    }
  };
  const enable = async () => {
    if (busyRef.current) return;
    const started = ++context.current;
    const isCurrent = () => context.current === started;
    busyRef.current = true;
    setBusy(true);
    setResult(null);
    try {
      const next = await enablePushNotifications({ isCurrent });
      if (!isCurrent()) return;
      setResult(next);
      const state = await getPushPermissionState();
      if (!isCurrent()) return;
      setPermission(state);
      if (next.code === 'registered') { setRegistration(true); setRegistrationError(false); }
    } catch { if (isCurrent()) setResult({ code: 'unavailable' }); }
    finally { if (isCurrent()) { busyRef.current = false; setBusy(false); } }
  };
  const messages = {
    registered: 'This device registration was acknowledged by the server for your account. This does not confirm notification delivery.',
    configuration_required: 'This app build cannot register for push notifications. A corrected signed app build is required; retrying will not fix this configuration.',
    native_failed: 'The device could not finish native registration. You can try again.',
    invalid_token: 'The device returned an invalid registration. You can try again.',
    upload_uncertain: 'Push setup could not be confirmed. Check setup status to safely resolve the same operation; no new registration is created.',
    context_failed: 'Setup has not started. Check your connection and try again.',
    superseded: 'This setup was superseded by a newer device change. You can start a new setup for this account.',
    status_unknown: 'The operation completed, but current device registration could not be checked. Check setup again before relying on push.',
    registration_needed: 'The earlier logout completed. You can now register this device for your account.',
    app_update_required: 'Update OpenRuns USA from the App Store to finish push setup. This installed app is missing a required native component; retrying cannot install it. Remote push cleanup has not been confirmed.',
    device_unavailable: 'This app could not read its device identity. Try again; if it persists, contact support.',

    storage_unavailable: 'Registration progress could not be saved on this device. No upload was started.',
    sign_in_required: 'Your sign-in must be checked before registering. Sign in again.',
    unknown: 'Notification permission could not be checked. Retry the permission check; this does not request permission or upload a token.',
    unavailable: 'Notification permission or registration is unavailable. No success has been confirmed.',
    busy: 'Another registration attempt is still running. Please wait before trying again.',
    abandoned: 'Registration ended because the account context changed. No success has been confirmed.',
  };
  const permissionConfig = PERMISSION_COPY[permission] || null;
  const PermissionIcon = permissionConfig?.icon;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-6"
      aria-labelledby="push-notification-heading"
      data-testid="push-notification-settings"
    >
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-6 w-6 shrink-0 text-orange-500" aria-hidden="true" />
        <div>
          <h2 id="push-notification-heading" className="font-semibold text-zinc-900">
            Push Notifications
          </h2>
          <p className="mt-1 text-sm text-zinc-600">
            Push notifications are the primary way OpenRuns keeps you updated.
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            Routine updates are delivered through push whenever possible.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2 rounded-lg bg-zinc-50 p-3 text-sm">
        {!permissionConfig ? (
          <p className="text-zinc-600" role="status">Checking this device&apos;s permission…</p>
        ) : (
          <p className={`flex items-start gap-2 ${permissionConfig.color}`}>
            <PermissionIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{permissionConfig.label}</span>
          </p>
        )}

        {registrationError ? (
          <p className="flex items-start gap-2 text-zinc-600">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Device registration status is temporarily unavailable.</span>
          </p>
        ) : registration === null ? (
          <p className="text-zinc-600" role="status">Checking registered devices…</p>
        ) : registration ? (
          <p className="flex items-start gap-2 text-green-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>An active iOS device is registered to this account.</span>
          </p>
        ) : (
          <p className="flex items-start gap-2 text-amber-700">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>No active iOS device is currently registered to this account.</span>
          </p>
        )}
      </div>

      {permission === 'unknown' && <button type="button" className="mt-3 min-h-11 rounded-lg border px-4 py-3" disabled={busy} onClick={retryPermissionCheck}>
        {busy ? 'Checking permission…' : 'Retry permission check'}
      </button>}
      {permission === 'denied'  && <p className="mt-3 text-sm">To allow notifications, open iPhone Settings → Notifications → Open Runs USA and turn on Allow Notifications. Then reopen this Notification Settings screen.</p>}
      {(permission === 'prompt' || permission === 'granted' || result?.code === 'upload_uncertain') && <div className="mt-4">
        <p className="mb-3 text-sm text-zinc-600">Enable notifications for session updates. If permission has not been requested, iOS will ask you to allow notifications. We then register this device to your signed-in account.</p>
        <button type="button" className="min-h-11 rounded-lg bg-orange-500 px-4 py-3 font-semibold text-white disabled:opacity-50"
          disabled={busy || (result && !result.retryable && result.code !== 'registered') || result?.code === 'registered'} onClick={enable}>
          {busy ? 'Checking setup…' : result?.code === 'upload_uncertain' ? 'Check setup status' : permission === 'prompt' ? 'Enable Push Notifications' : 'Register this device'}
        </button>
      </div>}
      {result && messages[result.code] && <p className="mt-3 text-sm" role="status">{messages[result.code]}</p>}
      <p className="mt-3 text-xs leading-5 text-zinc-500">
        Permission and device registration are separate. Neither status guarantees that every
        notification will appear on screen.
      </p>
    </section>
  );
};

export default PushNotificationSettings;
